/**
 * Utility functions for timestamp parsing and formatting
 */

/**
 * Convert MM:SS string to seconds
 * @param timestamp - Time string in MM:SS format
 * @returns Total seconds as number
 */
export function timestampToSeconds(timestamp: string): number {
  const parts = timestamp.split(':');
  if (parts.length !== 2) {
    return 0;
  }

  const minutes = parseInt(parts[0] || '0', 10);
  const seconds = parseInt(parts[1] || '0', 10);

  if (isNaN(minutes) || isNaN(seconds)) {
    return 0;
  }

  return minutes * 60 + seconds;
}

/**
 * Convert seconds to MM:SS string
 * @param seconds - Total seconds
 * @returns Formatted time string in MM:SS format
 */
export function secondsToTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Format duration in seconds to human-readable string
 * @param seconds - Duration in seconds
 * @returns Formatted string like "2.5s" or "1m 30s"
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${mins}m ${secs}s`;
}

/**
 * Parse duration string like "2.5s" to seconds
 * @param duration - Duration string (e.g., "2.5s", "1m 30s")
 * @returns Duration in seconds
 */
export function parseDuration(duration: string): number {
  const match = duration.match(/^(\d+\.?\d*)s$/);
  if (match && match[1]) {
    return parseFloat(match[1]);
  }

  return 0;
}
